/**
 * A alça é mesmo o alvo deste `pointerdown`? — a decisão que devolve o duplo toque ao elemento.
 *
 * ─── o defeito, medido ──────────────────────────────────────────────────────────────────────
 *
 * Card 1864882251313972786. Depois que o 1º toque seleciona um elemento, o overlay de seleção
 * nasce por cima dele com as alças de redimensionamento. O 2º toque, no CORPO do elemento, é
 * entregue pelo navegador a uma dessas alças — medido no `dev`, no instante do evento:
 *
 *     ponto do 2º toque      (593,227)
 *     alvo do evento         div.resize, alça `bottom`, retângulo (569,236) 42×6
 *     ponto dentro da alça?  NÃO — 9 px acima dela
 *
 * A alça chama `stopPropagation` e inicia o redimensionamento. O `ClickController`, que escuta
 * em bubble no `editor-host`, nunca vê o 2º `pointerdown`; a contagem fica em 1 e o
 * `doubleClick` não existe. E mesmo que ele contasse, o redimensionamento iniciado deixaria a
 * `activeInteraction` de pé — e o `InteractivityManager` descartaria o `dblclick`.
 *
 * O mouse não sofre disso (o 2º clique vai ao elemento), e o toque sofre em 100% das tentativas,
 * com qualquer intervalo entre os toques (0, 40, 110 e 250 ms) e com qualquer raio de contato
 * que o CDP aceita (1 a 20 px).
 *
 * ─── por que a decisão NÃO depende de saber o porquê ───────────────────────────────────────
 *
 * Por que o motor entrega o evento a um elemento que não está sob o ponto continua sem
 * explicação medida: não há `setPointerCapture` no `blocksuite`, e o raio de contato não muda
 * nada. O que é verificável, dentro do próprio evento, é o FATO: o ponto não está na alça.
 *
 * E o fato basta, porque ele descreve a intenção de quem tocou:
 *
 *   · dentro do retângulo da alça                → é a alça (redimensionar continua funcionando);
 *   · FORA do corpo do elemento                  → é a alça (o dedo foi buscá-la pelo lado de
 *     fora — e é aqui que o ajuste de alvo do navegador AJUDA quem quer redimensionar);
 *   · DENTRO do corpo e fora do retângulo da alça → não é a alça. O corpo do overlay é
 *     `pointer-events: none` justamente para que um toque ali chegue ao elemento.
 *
 * Mouse e caneta ficam exatamente como estão: para eles a decisão é sempre "a alça".
 *
 * ─── por que este arquivo não importa nada ──────────────────────────────────────────────────
 *
 * Mesmo motivo do `duplo-toque.ts` e do `decidir-pan.ts`: ele roda em
 * `cli/tests/js/alca-e-o-alvo.test.ts` sem `node_modules` do Affine e sem o build de ~16 min.
 * Nos dois arquivos do upstream fica só a pergunta.
 */

/** O mínimo de um retângulo de tela — o que `getBoundingClientRect()` devolve. */
export interface Retangulo {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface PerguntaDaAlca {
  /** `PointerEvent.pointerType`. */
  tipoDePonteiro: string | undefined;
  /** `PointerEvent.clientX` / `clientY` — o ponto que a pessoa tocou. */
  x: number;
  y: number;
  /** O retângulo da alça que recebeu o evento. `null` quando não se conseguiu medir. */
  alca: Retangulo | null;
  /** O retângulo do CORPO do elemento selecionado (a caixa do overlay). */
  corpo: Retangulo | null;
}

function contem(r: Retangulo, x: number, y: number): boolean {
  return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
}

/**
 * `true` = a alça é o alvo legítimo (comportamento de sempre: para o evento e redimensiona).
 * `false` = o toque é do ELEMENTO; a alça deve deixar o evento seguir.
 *
 * ⚠️ Na dúvida, `true`. Sem retângulo não há como afirmar que o ponto está fora — e devolver
 * `false` por falta de medida faria um redimensionamento legítimo deixar de funcionar para
 * consertar um duplo toque. A falha vai para o lado de sempre.
 */
export function aAlcaEhOAlvo({ tipoDePonteiro, x, y, alca, corpo }: PerguntaDaAlca): boolean {
  if (tipoDePonteiro !== 'touch') return true;
  if (!alca || !corpo) return true;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return true;

  if (contem(alca, x, y)) return true;
  if (!contem(corpo, x, y)) return true;

  return false;
}

/** O que os dois pontos de fiação precisam do DOM, declarado para o dublê do teste ser honesto. */
export interface EventoMinimo {
  pointerType?: string;
  clientX: number;
  clientY: number;
  composedPath?: () => unknown[];
}

interface NoMinimo {
  classList?: { contains(c: string): boolean };
  getBoundingClientRect?: () => Retangulo;
}

/**
 * A mesma pergunta, a partir do evento — é o que os dois pontos chamam.
 *
 * A alça é o primeiro `.handle` do caminho; o corpo é o `.affine-edgeless-selected-rect` do
 * mesmo caminho. Os dois pontos (a alça e o host do overlay) veem o MESMO `composedPath`, então
 * fazem a mesma pergunta e não podem discordar — se discordassem, a alça deixaria o evento
 * seguir e o host o pararia logo acima, e o defeito voltaria sem nada indicar por quê.
 */
export function aAlcaEhOAlvoDoEvento(e: EventoMinimo): boolean {
  try {
    const caminho = (e.composedPath?.() ?? []) as NoMinimo[];
    const achar = (classe: string) =>
      caminho.find(n => n?.classList?.contains?.(classe) ?? false) ?? null;

    const alca = achar('handle');
    const corpo = achar('affine-edgeless-selected-rect');

    return aAlcaEhOAlvo({
      tipoDePonteiro: e.pointerType,
      x: e.clientX,
      y: e.clientY,
      alca: alca?.getBoundingClientRect?.() ?? null,
      corpo: corpo?.getBoundingClientRect?.() ?? null,
    });
  } catch {
    // Uma medição que falha não pode desligar o redimensionamento — ver a nota acima.
    return true;
  }
}
